import { useFormik } from 'formik'
import { ErrorSection } from 'pages/loadKmz/cables-boxes/steppers/ErrorSection'
import { FC, memo, useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import {
	AreaTypeSelect,
	CountrySelect,
	emptyUfinetSelectOption,
	UfinetDropzone,
	WizardButtonNext,
	WizardButtonsWrapper,
} from 'ufinet-web-components'
import {
	AuthContext,
	IFileUploadData,
	IKmzAreaResponse,
	kmzAreaService,
	KmzMaxSize,
	useAqsError,
} from 'ufinet-web-functions'

import * as Yup from 'yup'

type Props = {
	settingData: Function
	wizardRef: any
	actionButtonRef: any
}
const AreaAddForm: FC<Props> = memo(
	({ settingData, wizardRef, actionButtonRef }) => {
		const intl = useIntl()
		const [error, setError] = useAqsError()
		const authData = useContext(AuthContext)
		const _kmzAreaService = kmzAreaService(authData)
		/*
		 ******************************
		 *************Formik***********
		 ******************************
		 */
		const dataFormSchema = Yup.object().shape({
			countrySelect: Yup.object().shape({
				label: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
			}),
			areaTypeSelect: Yup.object().shape({
				label: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
			}),
			file: Yup.mixed()
				.test('file size', `${intl.formatMessage({ id: 'ERROR.FILE.SIZE' })} 10MB`, (value) => {
					return value?.size <= KmzMaxSize
				})
				.test('file type', intl.formatMessage({ id: 'ERROR.FILE.EXTENSION' }), (value) => {
					return !value?.path?.split('.').pop()?.includes(['kmz', 'kml'])
				})
				.test('file required', intl.formatMessage({ id: 'ERROR.REQUIRED' }), (value) => value.size !== 0),
		})

		const formik = useFormik({
			initialValues: {
				countrySelect: emptyUfinetSelectOption,
				areaTypeSelect: emptyUfinetSelectOption,
				file: {
					path: '',
					size: 0,
				} as IFileUploadData,
			},
			validationSchema: dataFormSchema,
			onSubmit: (values) => {
				sendData(values)
			},
			validateOnChange: false,
			validateOnBlur: false,
		})
		/*
		 * end Formik
		 * */

		const sendData = (values: any) => {
			actionButtonRef.current.changeActionStatus()
			const formData = new FormData()

			formData.append('country', values.countrySelect.value)
			formData.append('areaType', values.areaTypeSelect.value)
			formData.append('file', values.file)

			_kmzAreaService
				.uploadKmz(formData)
				.then((response: IKmzAreaResponse) => {
					settingData({
						countryName: formik.values.countrySelect.label,
						typeId: formik.values.areaTypeSelect.value,
						filePath: formik.values.file.path.toString(),
						areas: response.areas,
						storageUrls: response.storageUrls,
					})
					wizardRef.current.goNext()
					return response
				})
				.catch((error) => {
					setError(error)
				})
				.finally(() => {
					actionButtonRef.current.changeActionStatus()
				})
		}

		const onChange = useCallback(
			(field: string) => (e: any) => {
				formik.setErrors({})
				formik.setFieldValue(field, e !== null ? e : {})
			},
			[]
		)

		return (
			<form onSubmit={formik.handleSubmit} className="container p-15 h-100 d-flex flex-column justify-content-center">
				<div className="row pb-10">
					<CountrySelect
						className="col-xl-6 col-12"
						onChange={onChange('countrySelect')}
						value={formik.values.countrySelect}
						error={formik.errors.countrySelect?.label}
					/>
					<AreaTypeSelect
						className="col-xl-6 col-12"
						onChange={onChange('areaTypeSelect')}
						value={formik.values.areaTypeSelect}
						error={formik.errors.areaTypeSelect?.label}
					/>
				</div>
				<div className="row">
					<div className="col-12">
						<UfinetDropzone
							onChange={onChange('file')}
							value={formik.values.file}
							error={formik.errors.file}
							text={intl.formatMessage({ id: 'STEPPER.LOAD_KMZ.DROPZONE' })}
							accept={{ 'application/vnd.google-earth': ['.kmz', '.kml'] }}
						/>
					</div>
				</div>
				<ErrorSection error={error} />
				<WizardButtonsWrapper>
					<div />
					<WizardButtonNext
						wizardRef={wizardRef}
						actionButtonRef={actionButtonRef}
						isDisabled={!formik.isValid}
						submitForm
					/>
				</WizardButtonsWrapper>
			</form>
		)
	},
	(prevProps, nextProps) => {
		return prevProps.settingData === nextProps.settingData
	}
)

export { AreaAddForm }
